import * as React from 'react';
import sustainApiRequest, {
  ApiError,
  ApiRequestOptions,
  ApiResponse,
} from './sustain-api-request';
import { alertUnhandledError, isNetworkError, logError } from '../util';
import { useTypedDispatch, useTypedSelector } from '../reducers';
import apiErrorToSentryExtras from './api-error-to-sentry-extras';

export default function useApiCallable() {
  const dispatch = useTypedDispatch();
  const user = useTypedSelector((state) => state.auth.user);

  const sessionId = user?.sessionId;
  return React.useCallback(
    async function callApi<R, W>(
      path: string,
      requestOptions?: ApiRequestOptions<W>,
      moreOptions?: {
        errorHandler?: (
          error: ApiError | Error,
          originalHandler: (err: ApiError | Error) => void,
        ) => void;
        addSubmitError?: (field: any, error: string) => void;
      },
    ): Promise<ApiResponse<R> | undefined> {
      function handleError(err: ApiError | Error) {
        const errData = (err as any).response?.data || {};

        if (err instanceof ApiError && err.response?.status === 401) {
          dispatch({ type: 'LOGOUT' });
        } else if (errData['@type'] === 'ConstraintViolation') {
          if (!moreOptions?.addSubmitError) {
            throw new Error(
              'handleError: Got a ConstraintViolation but no addSubmitError option',
            );
          }
          console.log('errData:', errData);
          errData.violations.forEach((violation: any) => {
            if (!moreOptions?.addSubmitError) {
              throw new Error(
                'handleError: Got a ConstraintViolation but no addSubmitError option',
              );
            }
            let message = violation.message;
            if (message.startsWith('This value should be of type')) {
              message = 'This value should not be blank';
            }
            moreOptions?.addSubmitError(
              violation.propertyPath || '_form',
              message,
            );
          });
        } else if (err instanceof ApiError && err.response?.status === 429) {
          window.alert(
            "You're doing that a little too often. Please wait a moment and try again.",
          );
        } else if (isNetworkError(err)) {
          window.alert(
            "There is a problem with your Internet connection, please make sure you're connected. Your changes may not have been saved.",
          );
        } else {
          const eventId = logError(err, 'callApi.unhandled', {
            extra: apiErrorToSentryExtras(err),
          });
          alertUnhandledError(eventId);
        }
      }

      try {
        const response = await sustainApiRequest<R, W>(path, {
          sessionId,
          ...requestOptions,
        });
        return response;
      } catch (err: any) {
        if (moreOptions?.errorHandler) {
          moreOptions.errorHandler(err, handleError);
        } else {
          handleError(err);
        }
        return undefined;
      }
    },
    [sessionId, dispatch],
  );
}
